import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from "@angular/core";
import { AbstractControl, FormControl, NonNullableFormBuilder, Validators } from "@angular/forms";
import { BehaviorSubject, Subscription } from "rxjs";
import { ExtensionsListComponent } from "@onsip/web/features/shared/components/extensionsList/extensions-list.component";
import { TranslateService } from "@ngx-translate/core";
import { RecordingManagementService } from "@onsip/common/services/api/resources/recordingManager/recording-manager.service";
import {
  RecManagement,
  RecManagementParams
} from "@onsip/common/services/api/resources/recordingManager/recording-manager";
import { SnackbarService } from "@onsip/web/features/shared/components/snackbar/snackbar.service";
import { AddressSummaryService } from "@onsip/common/services/api/resources/addressSummary/address-summary.service";
import { take } from "rxjs/operators";
import { views } from "@onsip/web/app/phone/views";
import { MatDialog } from "@angular/material/dialog";
import { ModalMaterialComponent } from "@onsip/web/features/shared/components/modal/modal-material.component";

@Component({
  selector: "onsip-recording-manager-edit",
  templateUrl: "./recording-manager-edit.component.html",
  styleUrls: ["./recording-manager-edit.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class RecordingManagerEditComponent implements OnInit, OnDestroy {
  @Input() recManager!: RecManagement;
  @Output() editPanel = new EventEmitter<void>();
  protected unsubscriber = new Subscription();
  @ViewChild("extensionsList") extensionsListRef!: ExtensionsListComponent;
  addressId$ = new BehaviorSubject("");
  loadingStateComplete$ = new BehaviorSubject(true);
  views = views;

  recManagementForm = this.fb.group({
    sipAddress: ["", [Validators.required]],
    pin: [
      "",
      [
        Validators.required,
        Validators.pattern("^[0-9]*$"),
        Validators.minLength(1),
        Validators.maxLength(4)
      ]
    ],
    extensions: [<Array<string>>[]],
    name: [""]
  });
  constructor(
    private translate: TranslateService,
    private recManagementService: RecordingManagementService,
    private snackbar: SnackbarService,
    private addressSummaryService: AddressSummaryService,
    private fb: NonNullableFormBuilder,
    public dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.recManagementService.recordingManagementBrowse();
    this.addressSummaryService.addressSummaryBrowse();
    this.fillForm();
  }

  ngOnDestroy(): void {
    this.unsubscriber.unsubscribe();
  }

  saveForm(): void {
    if (this.recManagementForm.invalid) return;
    this.loadingStateComplete$.next(false);
    const params: RecManagementParams = {
      Pin: Number(this.pin.value),
      Address: `${this.recManager.username}@${this.recManager.domain}`,
      Name: this.recManagementForm.controls.name.value,
      Username: this.sipAddress.value
    };
    this.recManagementService.recordingManagementEdit(params).then(res => {
      if (res && res.status === "success") {
        this.extensionsListRef.editExtensions(this.sipAddress.value);
        this.snackbar.openSnackBar(
          `${this.translate.instant("ONSIP_I18N.REC_MANAGEMENT_WAS_UPDATED")}`,
          "success"
        );
        this.editPanel.emit();
      } else {
        this.snackbar.openSnackBar(res.data.message as string, "error");
        this.recManagementService.clearErrors();
      }
      this.loadingStateComplete$.next(true);
    });
  }

  get sipAddress(): FormControl {
    return this.recManagementForm.get("sipAddress") as FormControl;
  }

  get extensions(): FormControl {
    return this.recManagementForm.get("extensions") as FormControl;
  }

  get pin(): FormControl {
    return this.recManagementForm.get("pin") as FormControl;
  }

  getGenericError(control: AbstractControl, value: string): string {
    return control.hasError("required")
      ? this.translate.instant("ONSIP_I18N._VALUE__IS_REQUIRED", { value })
      : control.hasError("pattern")
      ? this.translate.instant("ONSIP_I18N.PIN_MUST_CONTAIN_ONLY_NUMBERS")
      : control.hasError("maxlength")
      ? this.translate.instant("ONSIP_I18N.VALUE_IS_LONGER_THAN_LENGTH_CHARACTERS", {
          value,
          length: control.errors?.maxlength.requiredLength
        })
      : "";
  }

  cancel(): void {
    this.unsubscriber.add(
      this.dialog
        .open(ModalMaterialComponent, {
          panelClass: ["mat-typography", "onsip-dialog-universal-style"],
          data: {
            title: this.translate.instant("ONSIP_I18N.CANCEL_EDITS"),
            message: this.translate.instant(
              "ONSIP_I18N.ANY_CHANGES_YOU_HAVE_MADE_WILL_NOT_BE_SAVED"
            ),
            primaryBtnText: this.translate.instant("ONSIP_I18N.OK"),
            secondaryBtnText: this.translate.instant("ONSIP_I18N.KEEP_EDITING"),
            primaryBtnFlat: true
          }
        })
        .afterClosed()
        .pipe(take(1))
        .subscribe(retObj => {
          if (retObj?.doPrimaryAction) {
            this.editPanel.emit();
          }
        })
    );
  }

  private fillForm() {
    this.recManagementForm.patchValue({
      sipAddress: this.recManager.username,
      pin: this.recManager.pin,
      name: this.recManager.name
    });
    this.addressId$.next(this.recManager.addressId);
  }
}
